'use client';
import React, { useRef } from 'react';
import FilmCard from '@/components/ui/FilmCard';
import type { FilmCardData } from '@/components/ui/FilmCard';
import Icon from '@/components/ui/AppIcon';

interface FilmRailProps {
  title: string;
  subtitle?: string;
  films: FilmCardData[];
  cardSize?: 'sm' | 'md' | 'lg';
  watchlistIds?: Set<string>;
  onToggleWatchlist?: (filmId: string) => void;
}

export default function FilmRail({ title, subtitle, films, cardSize = 'md', watchlistIds, onToggleWatchlist }: FilmRailProps) {
  const scrollRef = useRef<HTMLDivElement>(null);

  const scroll = (dir: 'left' | 'right') => {
    if (!scrollRef.current) return;
    const amount = scrollRef.current.clientWidth * 0.75;
    scrollRef.current.scrollBy({ left: dir === 'right' ? amount : -amount, behavior: 'smooth' });
  };

  if (!films.length) return null;

  return (
    <div className="relative group/rail px-4 sm:px-6 lg:px-10">
      {/* Header */}
      <div className="flex items-end justify-between mb-3 sm:mb-4">
        <div>
          <h2 className="text-sm sm:text-base font-bold text-foreground leading-tight">{title}</h2>
          {subtitle && <p className="text-xs text-muted-foreground mt-0.5 hidden sm:block">{subtitle}</p>}
        </div>
        {/* Scroll arrows — desktop only */}
        <div className="hidden sm:flex items-center gap-1 opacity-0 group-hover/rail:opacity-100 transition-opacity">
          <button
            onClick={() => scroll('left')}
            className="w-7 h-7 rounded-full bg-secondary border border-border flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
            aria-label="Scroll left"
          >
            <Icon name="ChevronLeftIcon" size={14} />
          </button>
          <button
            onClick={() => scroll('right')}
            className="w-7 h-7 rounded-full bg-secondary border border-border flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
            aria-label="Scroll right"
          >
            <Icon name="ChevronRightIcon" size={14} />
          </button>
        </div>
      </div>

      {/* Scrollable row */}
      <div
        ref={scrollRef}
        className="flex gap-2 sm:gap-3 overflow-x-auto scrollbar-hide pb-1"
        style={{ scrollSnapType: 'x mandatory' }}
      >
        {films.map(film => (
          <div key={film.id} style={{ scrollSnapAlign: 'start' }}>
            <FilmCard
              film={film}
              size={cardSize}
              inWatchlist={watchlistIds ? watchlistIds.has(film.id) : undefined}
              onToggleWatchlist={onToggleWatchlist}
            />
          </div>
        ))}
      </div>
    </div>
  );
}